import type { LucideIcon } from 'lucide-react';
import {
  BadgeCheck,
  BookOpenText,
  Calculator,
  FileSpreadsheet,
  LayoutDashboard,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  Target,
} from 'lucide-react';

export type EuTaxonomySectionId =
  | 'overview'
  | 'onboarding'
  | 'activity-identification'
  | 'eligibility-assessment'
  | 'substantial-contribution'
  | 'dnsh-principle'
  | 'minimum-safeguards'
  | 'financial-kpis'
  | 'disclosure-tables';

export interface EuTaxonomyMetric {
  label: string;
  value: string;
  detail: string;
}

export interface EuTaxonomySectionDefinition {
  id: EuTaxonomySectionId;
  to: string;
  label: string;
  title: string;
  description: string;
  status: string;
  owner: string;
  reportCycle: string;
  completion: number;
  icon: LucideIcon;
  metrics: EuTaxonomyMetric[];
  priorities: string[];
  deliverables: string[];
}

export const EU_TAXONOMY_SECTIONS: EuTaxonomySectionDefinition[] = [
  {
    id: 'overview',
    to: '/eu-taxonomy/overview',
    label: 'Overview',
    title: 'What EU Taxonomy Is and How It Works',
    description:
      'An introduction to the EU classification system for environmentally sustainable activities, the link with CSRD, and the path from eligibility to disclosure-ready KPI reporting.',
    status: 'Framework introduction',
    owner: 'Sustainability Office and Finance',
    reportCycle: 'Annual reporting cycle',
    completion: 28,
    icon: LayoutDashboard,
    metrics: [
      {
        label: 'Environmental objectives',
        value: '6',
        detail: 'Climate, water, circular economy, pollution, and biodiversity objectives covered by the framework',
      },
      {
        label: 'Reporting steps',
        value: '7',
        detail: 'From activity identification to the final disclosure tables',
      },
      {
        label: 'Financial KPIs',
        value: '3',
        detail: 'Turnover, CapEx, and OpEx are disclosed separately for eligible and aligned shares',
      },
      {
        label: 'Alignment gates',
        value: '4',
        detail: 'Eligibility, substantial contribution, DNSH, and minimum safeguards shape the final outcome',
      },
    ],
    priorities: [
      'Explain clearly that CSRD creates the disclosure obligation while Taxonomy defines what can be classified as environmentally sustainable.',
      'Keep the distinction between eligible and aligned visible from the first screen so users do not overstate green performance.',
      'Show the full reporting journey before users move into activity-by-activity assessment.',
    ],
    deliverables: [
      'Clear introduction to the purpose and scope of EU Taxonomy.',
      'Visual explanation of the eligibility-to-alignment logic and environmental objectives.',
      'Practical route into the working sections of the module.',
    ],
  },
  {
    id: 'onboarding',
    to: '/eu-taxonomy/onboarding',
    label: 'Onboarding',
    title: 'Leon-guided Taxonomy Onboarding',
    description:
      'Leon asks structured questions about business activities, assets, projects, and daily OpEx, then builds an editable draft activity and NACE mapping table.',
    status: 'Starter intake',
    owner: 'Taxonomy Lead and Business Owners',
    reportCycle: 'At setup and major structural change',
    completion: 32,
    icon: Sparkles,
    metrics: [
      {
        label: 'Core prompts',
        value: '4',
        detail: 'Business activity, assets, CapEx projects, and green OpEx prompts',
      },
      {
        label: 'Editable outputs',
        value: '5',
        detail: 'Draft activity rows already structured into KPI buckets',
      },
      {
        label: 'AI guide',
        value: 'Leon',
        detail: 'Conversation-first intake that users can always edit manually',
      },
      {
        label: 'Target result',
        value: 'NACE table',
        detail: 'Activity list with sector, business lens, and taxonomy KPI classification',
      },
    ],
    priorities: [
      'Walk the company department by department instead of collecting generic legal descriptions.',
      'Separate what the company sells from what it owns, builds, modernizes, and maintains.',
      'Leave the draft editable so finance and operations can refine the mapping before eligibility screening starts.',
    ],
    deliverables: [
      'Starter taxonomy intake profile.',
      'Editable draft activity register with NACE mapping.',
      'Clear handoff into the eligibility assessment workflow.',
    ],
  },
  {
    id: 'activity-identification',
    to: '/eu-taxonomy/activity-identification',
    label: 'Activity Identification',
    title: 'Activity Identification',
    description:
      'Capture business activities, assets, projects, and daily operations in one worksheet, then confirm the activity universe and lock which operations belong to turnover, CapEx, and OpEx reporting.',
    status: 'Intake and scoping',
    owner: 'Operations and Finance',
    reportCycle: 'Annual with event-driven updates',
    completion: 38,
    icon: BookOpenText,
    metrics: [
      { label: 'Worksheet prompts', value: '4', detail: 'Business activity, assets, CapEx projects, and daily operations prompts' },
      { label: 'Draft activity rows', value: '5', detail: 'Editable activity mapping table with NACE and business lens' },
      { label: 'Asset clusters', value: '5', detail: 'Owned and leased asset groups relevant to taxonomy' },
      { label: 'Review status', value: 'Draft', detail: 'Activity mapping still awaiting owner confirmation' },
    ],
    priorities: [
      'Walk the company department by department before making any eligibility judgement.',
      'Separate what the company sells from what it owns, builds, modernizes, and maintains.',
      'Avoid mixing unrelated business lines under one taxonomy activity just because they share one department.',
      'Use business reality first, then connect to delegated act language and NACE labels.',
      'Keep supporting notes for every activity split so later KPI reconciliation is defensible.',
    ],
    deliverables: [
      'Completed activity identification worksheet.',
      'Editable draft activity register with NACE mapping.',
      'Locked KPI bucket assignment for each activity.',
      'Owner-approved activity notes for finance and assurance.',
    ],
  },
  {
    id: 'eligibility-assessment',
    to: '/eu-taxonomy/eligibility-assessment',
    label: 'Eligibility Assessment',
    title: 'Eligibility Assessment',
    description:
      'Match each activity against delegated acts, identify potentially eligible activities, and flag where evidence is still required.',
    status: 'Screening',
    owner: 'Taxonomy Lead',
    reportCycle: 'Annual refresh',
    completion: 41,
    icon: ScanSearch,
    metrics: [
      { label: 'Activities screened', value: '12', detail: 'All draft rows passed through first-pass eligibility logic' },
      { label: 'Potentially eligible', value: '8', detail: 'Rows already linked to delegated act chapters' },
      { label: 'Needs research', value: '3', detail: 'Cases where activity descriptions remain too broad' },
      { label: 'Not in scope', value: '1', detail: 'One support activity not covered by current acts' },
    ],
    priorities: [
      'Make the delegated act reference explicit for every positive screening result.',
      'Separate uncertain matches from confirmed non-eligible activities.',
      'Track missing evidence now to avoid late-stage rework during KPI preparation.',
    ],
    deliverables: [
      'Eligibility decision register.',
      'List of evidence gaps per activity.',
      'Confirmed list of potentially eligible activities for alignment testing.',
    ],
  },
  {
    id: 'substantial-contribution',
    to: '/eu-taxonomy/substantial-contribution',
    label: 'Substantial Contribution',
    title: 'Substantial Contribution',
    description:
      'Assess technical screening criteria and show how each eligible activity contributes to the relevant environmental objective.',
    status: 'Technical review',
    owner: 'Engineering and Sustainability',
    reportCycle: 'Annual plus criteria changes',
    completion: 24,
    icon: Target,
    metrics: [
      { label: 'Objectives in use', value: '3', detail: 'Climate mitigation, adaptation, and circularity scenarios' },
      { label: 'Criteria packs', value: '8', detail: 'Technical screening checklists opened' },
      { label: 'Evidence files', value: '19', detail: 'Policies, specs, and engineering data linked' },
      { label: 'Awaiting owner', value: '5', detail: 'Criteria still require operational confirmation' },
    ],
    priorities: [
      'Translate legal criteria into operational evidence requests that business teams can actually answer.',
      'Keep objective-level rationale alongside each criteria set.',
      'Use structured pass, fail, and uncertain states rather than narrative-only commentary.',
    ],
    deliverables: [
      'Technical screening assessment by activity.',
      'Objective-level rationale and evidence links.',
      'Draft substantial contribution conclusions ready for review.',
    ],
  },
  {
    id: 'dnsh-principle',
    to: '/eu-taxonomy/dnsh-principle',
    label: 'DNSH Principle',
    title: 'Do No Significant Harm',
    description:
      'Evaluate DNSH criteria across environmental objectives and capture the controls, evidence, and exceptions required for alignment.',
    status: 'Control review',
    owner: 'Environment, Health and Safety',
    reportCycle: 'Annual and incident-driven updates',
    completion: 18,
    icon: BadgeCheck,
    metrics: [
      { label: 'DNSH packs', value: '6', detail: 'Objective-specific screening packs prepared' },
      { label: 'High-risk gaps', value: '2', detail: 'Water and pollution evidence still incomplete' },
      { label: 'Linked controls', value: '14', detail: 'Operating controls reused from compliance and HSE domains' },
      { label: 'Exceptions', value: '0', detail: 'No waivers documented in current draft' },
    ],
    priorities: [
      'Treat DNSH as a separate evidence exercise, not as a note attached to substantial contribution.',
      'Reuse operational controls and environmental permits wherever possible.',
      'Keep unresolved DNSH issues visible because they directly affect alignment conclusions.',
    ],
    deliverables: [
      'DNSH screening matrix by activity.',
      'Control and evidence mapping for each objective.',
      'Escalation list for unresolved harm-prevention issues.',
    ],
  },
  {
    id: 'minimum-safeguards',
    to: '/eu-taxonomy/minimum-safeguards',
    label: 'Minimum Safeguards',
    title: 'Minimum Safeguards',
    description:
      'Complete the Minimum Safeguards verification worksheet in the same structure as the spreadsheet, covering due diligence, human rights, labour, anti-corruption, consumer, competition, and tax controls.',
    status: 'Worksheet in progress',
    owner: 'Legal, Compliance, HR, and Procurement',
    reportCycle: 'Annual governance review',
    completion: 61,
    icon: ShieldCheck,
    metrics: [
      {
        label: 'Worksheet sections',
        value: '8',
        detail: 'Due diligence, environment, labour, technology, anti-corruption, consumer, competition, and tax.',
      },
      {
        label: 'Worksheet questions',
        value: '66',
        detail: 'Questions translated into English and grouped like the original Excel file.',
      },
      {
        label: 'Editable columns',
        value: '4',
        detail: 'Self-assessment, supporting document, company notes, and reviewer notes.',
      },
      {
        label: 'Gate logic',
        value: 'Article 18',
        detail: 'The completed worksheet supports the final enterprise-level safeguards decision.',
      },
    ],
    priorities: [
      'Treat minimum safeguards as a single enterprise gate, not a per-activity checklist.',
      'Separate company self-assessment from reviewer verdict so positive answers do not mask evidence gaps.',
      'Connect every blocking gap to a remediation owner before KPI and disclosure sign-off.',
    ],
    deliverables: [
      'Article 18 decision record with enterprise pass or block verdict.',
      'Due diligence and OECD coverage gap log with remediation owners.',
      'Cross-module rule showing the impact on KPI and disclosure outputs.',
    ],
  },
  {
    id: 'financial-kpis',
    to: '/eu-taxonomy/financial-kpis',
    label: 'Financial KPIs',
    title: 'Financial KPIs',
    description:
      'Prepare turnover, CapEx, and OpEx calculations, reconcile numerator and denominator logic, and keep source assumptions auditable.',
    status: 'Finance preparation',
    owner: 'Finance and Controlling',
    reportCycle: 'Quarterly tracking, annual disclosure',
    completion: 26,
    icon: Calculator,
    metrics: [
      { label: 'KPI sets', value: '3', detail: 'Turnover, CapEx, and OpEx models configured' },
      { label: 'Reconciliations', value: '7', detail: 'Links to ledger and management reporting packs' },
      { label: 'Assumptions', value: '11', detail: 'Calculation assumptions requiring sign-off' },
      { label: 'Draft coverage', value: '61%', detail: 'Current share of values already mapped to activity rows' },
    ],
    priorities: [
      'Keep denominator logic transparent before debating aligned percentages.',
      'Separate raw finance extraction from taxonomy judgement layers.',
      'Version assumptions so the disclosure table always has a defensible calculation trail.',
    ],
    deliverables: [
      'Draft KPI calculation workbook inside the module.',
      'Reconciled numerator and denominator notes.',
      'Management review pack for KPI sign-off.',
    ],
  },
  {
    id: 'disclosure-tables',
    to: '/eu-taxonomy/disclosure-tables',
    label: 'Disclosure Tables',
    title: 'Disclosure Tables',
    description:
      'Assemble the final disclosure tables, expose the link to underlying activity and KPI logic, and prepare an audit-ready export pack.',
    status: 'Final assembly',
    owner: 'Reporting Lead',
    reportCycle: 'Draft and publication milestones',
    completion: 14,
    icon: FileSpreadsheet,
    metrics: [
      { label: 'Table packs', value: '4', detail: 'Main KPI and activity disclosure layouts prepared' },
      { label: 'Linked rows', value: '27', detail: 'Rows already connected to activities, criteria, and finance logic' },
      { label: 'Narrative notes', value: '9', detail: 'Explanatory notes drafted for assumptions and limitations' },
      { label: 'Export status', value: 'Not started', detail: 'Final reporting package still awaiting review sign-off' },
    ],
    priorities: [
      'Build disclosure tables from approved workflow state rather than manual spreadsheet reshaping.',
      'Keep every published row traceable back to activity, criteria, and finance evidence.',
      'Prepare clear narrative notes for assumptions, exclusions, and year-on-year changes.',
    ],
    deliverables: [
      'EU Taxonomy disclosure tables for publication.',
      'Audit-ready export bundle with traceability.',
      'Narrative note set for management and assurance review.',
    ],
  },
];

export const EU_TAXONOMY_NAV_SECTIONS = EU_TAXONOMY_SECTIONS.filter(
  (section) => section.id !== 'onboarding'
);

export function isEuTaxonomySection(sectionId: string): sectionId is EuTaxonomySectionId {
  return EU_TAXONOMY_SECTIONS.some((section) => section.id === sectionId);
}

export function getEuTaxonomySection(sectionId: EuTaxonomySectionId): EuTaxonomySectionDefinition {
  return EU_TAXONOMY_SECTIONS.find((section) => section.id === sectionId) ?? EU_TAXONOMY_SECTIONS[0];
}
